
import React from 'react';
import { Icon } from '@iconify/react';
import { MappedLeadData } from '../../types';
import { CardHeader, SimpleTabContent } from '../common/Atomic';
import { ResearchSummarySections } from '../common/ResearchSummarySections';
import { StreamdownRendererSimple } from '../common/StreamdownRenderer';
import {
    cleanResearchSummaryContent,
    getResearchSummarySectionsForTab,
    isStandaloneResearchSummaryTitle,
} from '../../utils/researchSummary';

interface PersonTabProps {
  data: MappedLeadData;
}

interface ProfessionalSnapshotFact {
    label: string | null;
    value: string;
}

const PERSON_SUMMARY_MARKER_PATTERN = /^(📊|🎯|🔍|⚡)/;
const GENERATED_TITLE_KEYCAP_PATTERN = /[0-9#*]\uFE0F?\u20E3/gu;
const GENERATED_TITLE_EMOJI_PATTERN = /[\p{Extended_Pictographic}\p{Emoji_Presentation}\p{Emoji_Modifier}\uFE0F\u200D\u20E3]/gu;

const cleanGeneratedSectionTitle = (title: string) => title
    .replace(PERSON_SUMMARY_MARKER_PATTERN, '')
    .replace(/\*\*/g, '')
    .replace(/__/g, '')
    .replace(GENERATED_TITLE_KEYCAP_PATTERN, ' ')
    .replace(GENERATED_TITLE_EMOJI_PATTERN, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const cleanSnapshotFragment = (fragment: string) => fragment
    .trim()
    .replace(/^[-*•]\s+/, '')
    .replace(/^#+\s*/, '')
    .replace(/\*\*/g, '')
    .trim();

const parseProfessionalSnapshotFacts = (content: string): ProfessionalSnapshotFact[] => content
    .split(/\n+/)
    .flatMap((line) => {
        const cleanedLine = cleanSnapshotFragment(line);
        if (!cleanedLine) return [];

        const pipeSeparatedFragments = cleanedLine
            .split(/\s+\|\s+/)
            .map(cleanSnapshotFragment)
            .filter(Boolean);
        const allFragmentsAreFacts = pipeSeparatedFragments.length > 1 && pipeSeparatedFragments.every(
            (fragment) => /^[^:]{2,48}:\s*\S/.test(fragment)
        );

        return allFragmentsAreFacts ? pipeSeparatedFragments : [cleanedLine];
    })
    .map((fragment) => {
        const labelMatch = fragment.match(/^([^:]{2,48}):\s*(.+)$/);
        if (!labelMatch) return { label: null, value: fragment };

        return {
            label: labelMatch[1].trim(),
            value: labelMatch[2].trim(),
        };
    });

export const PersonTab: React.FC<PersonTabProps> = ({ data }) => {
    const headingIdPrefix = React.useId();
    const personOverviewHeadingId = `${headingIdPrefix}-person-overview`;
    const professionalSnapshotHeadingId = `${headingIdPrefix}-professional-snapshot`;

    // Parse the generator summary
    const parseSummarySections = (summary: string) => {
        if (!summary) return [];
        const parts = summary
            .split(/(?=^[ \t]*(?:(?:\*\*|__)[ \t]*)?(?:📊|🎯|🔍|⚡))/gm)
            .filter(p => p.trim().length > 0);
        if (parts.length <= 1 && summary.includes('###')) {
            return summary.split('###').filter(p => p.trim().length > 0).map(p => {
                const lines = p.trim().split('\n');
                let contentLines = lines.slice(1);
                let title = cleanGeneratedSectionTitle(lines[0]);
                const firstContentLineIndex = contentLines.findIndex((line) => line.trim().length > 0);
                const firstContentLine = firstContentLineIndex >= 0 ? contentLines[firstContentLineIndex].trim() : '';
                const firstContentLineLooksLikeTitle = isStandaloneResearchSummaryTitle(firstContentLine);

                if (!title && firstContentLineLooksLikeTitle) {
                    title = cleanGeneratedSectionTitle(firstContentLine);
                    contentLines = contentLines.slice(firstContentLineIndex + 1);
                }

                return {
                    icon: '📄',
                    title,
                    content: cleanResearchSummaryContent(contentLines.join('\n')),
                };
            }).filter((section) => section.title || section.content);
        }
        return parts.map(part => {
            const lines = part.trim().split('\n');
            const headerLine = lines[0];
            const iconMatch = headerLine.match(/(?:📊|🎯|🔍|⚡)/);
            const icon = iconMatch ? iconMatch[0] : '📄';
            let contentLines = lines.slice(1);
            let title = cleanGeneratedSectionTitle(headerLine);
            const firstContentLineIndex = contentLines.findIndex((line) => line.trim().length > 0);
            const firstContentLine = firstContentLineIndex >= 0 ? contentLines[firstContentLineIndex].trim() : '';
            const firstContentLineLooksLikeTitle = isStandaloneResearchSummaryTitle(firstContentLine);

            if (!title && firstContentLineLooksLikeTitle) {
                title = cleanGeneratedSectionTitle(firstContentLine);
                contentLines = contentLines.slice(firstContentLineIndex + 1);
            }

            const content = cleanResearchSummaryContent(contentLines.join('\n'));
            return { icon, title, content };
        }).filter((section) => section.title || section.content);
    };

    // Extract Professional Snapshot section specifically
    const allSections = parseSummarySections(data.linkedinSummary);
    const researchSections = getResearchSummarySectionsForTab(data, 'person');
    const professionalSnapshotSection = allSections.find(section =>
        section.title.toLowerCase().includes('professional snapshot')
    );

    // Filter out engagement and network sections - they belong in Signals tab
    const summarySections = allSections
        .filter(section => {
            const title = section.title.toLowerCase();
            // Exclude sections that belong in Signals tab OR Professional Snapshot (shown separately)
            return !title.includes('engagement') &&
                   !title.includes('network') &&
                   !title.includes('signal') &&
                   !title.includes('professional snapshot');
        });
    const hasSections = summarySections.length > 0;
    const proseMeasureClass = 'max-w-[62ch] text-sm leading-7 text-gray-300';
    const personOverview = data.personLvl2Overview?.trim() || '';
    const overviewParagraphs = personOverview
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);
    const snapshotFacts = professionalSnapshotSection
        ? parseProfessionalSnapshotFacts(professionalSnapshotSection.content)
        : [];
    const hasStructuredSnapshot = snapshotFacts.some((fact) => fact.label);
    const hasOverviewAndSnapshot = Boolean(personOverview && professionalSnapshotSection);

    return (
        <SimpleTabContent>
            <div className="space-y-5">
                <div className={`grid grid-cols-1 gap-5 ${hasOverviewAndSnapshot ? 'xl:grid-cols-[minmax(0,3fr)_minmax(15rem,1fr)] xl:items-start' : ''}`}>
                    {/* Lead Intelligence Overview - Priority display of lvl2 overview */}
                    {personOverview && (
                        <section
                            aria-labelledby={personOverviewHeadingId}
                            className="min-w-0 rounded-[20px] border border-white/[0.05] bg-white/[0.03] p-4 sm:p-5"
                        >
                            <div className="mb-4 flex items-start gap-3">
                                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#5260FF]/10 text-[#7F8BFF]">
                                    <Icon icon="bx:user" className="h-5 w-5" aria-hidden="true" />
                                </div>
                                <div className="min-w-0 pt-0.5">
                                    <h3 id={personOverviewHeadingId} className="text-xs font-bold uppercase tracking-[0.14em] text-[#AEB9FF]">Person Overview</h3>
                                    <p className="mt-1 text-[11px] leading-4 text-[#9AA5C4]">Role, priorities, and context at a glance.</p>
                                </div>
                            </div>
                            <div className="space-y-3 sm:pl-12">
                                {overviewParagraphs.map((paragraph, index) => (
                                    <p key={index} className="max-w-[80ch] whitespace-pre-line text-pretty text-sm leading-6 text-gray-300">
                                        {paragraph}
                                    </p>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Professional Snapshot Section - from linkedin_summary */}
                    {professionalSnapshotSection && (
                        <section
                            aria-labelledby={professionalSnapshotHeadingId}
                            className="min-w-0 rounded-[20px] border border-[#5260FF]/15 bg-[linear-gradient(180deg,rgba(82,96,255,0.07),rgba(255,255,255,0.025))] p-4 sm:p-5"
                        >
                            <div className="mb-4 flex items-start gap-3">
                                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#5260FF]/12 text-[#8F9AFF]">
                                    <Icon icon="bx:bar-chart-alt-2" className="h-5 w-5" aria-hidden="true" />
                                </div>
                                <div className="min-w-0 pt-0.5">
                                    <h3 id={professionalSnapshotHeadingId} className="text-xs font-bold uppercase tracking-[0.12em] text-[#AEB9FF]">Professional Snapshot</h3>
                                    <p className="mt-1 text-[11px] leading-4 text-[#9AA5C4]">Career facts at a glance.</p>
                                </div>
                            </div>

                            {hasStructuredSnapshot ? (
                                <dl className={`grid min-w-0 gap-x-4 gap-y-3 sm:grid-cols-2 ${hasOverviewAndSnapshot ? 'xl:grid-cols-1' : ''}`}>
                                    {snapshotFacts.map((fact, index) => (
                                        <div key={`${fact.label || 'detail'}-${index}`} className="min-w-0 rounded-xl bg-black/10 px-3 py-2.5">
                                            <dt className={fact.label ? 'text-[10px] font-bold uppercase tracking-[0.14em] text-[#8E98B5]' : 'sr-only'}>
                                                {fact.label || 'Profile detail'}
                                            </dt>
                                            <dd className={`${fact.label ? 'mt-1' : ''} break-words text-[13px] leading-5 text-gray-200`}>
                                                <StreamdownRendererSimple
                                                    content={fact.value}
                                                    className="[&_.space-y-4]:space-y-1 [&_a]:text-[#8EA4FF] [&_p]:my-0 [&_p]:break-words [&_p]:text-[13px] [&_p]:leading-5"
                                                />
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            ) : (
                                <div className="min-w-0 break-words text-[13px] leading-5 text-gray-200">
                                    <StreamdownRendererSimple
                                        content={professionalSnapshotSection.content}
                                        className="[&_.space-y-4]:space-y-2 [&_li]:text-[13px] [&_li]:leading-5 [&_p]:my-0 [&_p]:break-words [&_p]:whitespace-pre-line [&_p]:text-[13px] [&_p]:leading-5"
                                    />
                                </div>
                            )}
                        </section>
                    )}
                </div>

                <ResearchSummarySections
                    title="Person Research"
                    sections={researchSections}
                    tab="person"
                />

                {/* Dynamic Person Summary Cards */}
                {hasSections && (
                    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                        {summarySections.map((section, idx) => {
                            const normalizedSectionTitle = section.title.toLowerCase();
                            let cardTheme = {
                                icon: 'bx:info-circle',
                                iconBg: 'bg-gray-400/10',
                                iconColor: 'text-gray-400',
                                titleColor: 'text-gray-300',
                            };

                            if (section.icon === '📊' || normalizedSectionTitle.includes('snapshot')) {
                                cardTheme = { icon: 'bx:bar-chart-alt-2', iconBg: 'bg-[#5260FF]/10', iconColor: 'text-[#7F8BFF]', titleColor: 'text-[#AEB9FF]' };
                            } else if (section.icon === '🎯' || normalizedSectionTitle.includes('insights') || normalizedSectionTitle.includes('outreach')) {
                                cardTheme = { icon: 'bx:target-lock', iconBg: 'bg-[#15FBFF]/10', iconColor: 'text-[#15FBFF]', titleColor: 'text-cyan-100' };
                            } else if (section.icon === '🔍' || normalizedSectionTitle.includes('takeaways')) {
                                cardTheme = { icon: 'bx:search-alt', iconBg: 'bg-purple-500/10', iconColor: 'text-purple-400', titleColor: 'text-purple-100' };
                            } else if (section.icon === '⚡' || normalizedSectionTitle.includes('opportunities')) {
                                cardTheme = { icon: 'bx:bolt-circle', iconBg: 'bg-amber-500/10', iconColor: 'text-amber-400', titleColor: 'text-amber-100' };
                            }

                            return (
                                <div key={idx} className="rounded-2xl bg-white/[0.03] p-6">
                                    <div className="mb-5 flex items-start gap-4">
                                        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${cardTheme.iconBg} ${cardTheme.iconColor}`}>
                                            <Icon icon={cardTheme.icon} className="h-5 w-5" aria-hidden="true" />
                                        </div>
                                        <div className="pt-1">
                                            <h3 className={`text-sm font-bold uppercase tracking-[0.14em] ${cardTheme.titleColor}`}>{section.title}</h3>
                                        </div>
                                    </div>

                                    <div className={`pl-0 md:pl-[56px] ${proseMeasureClass}`}>
                                        <StreamdownRendererSimple content={section.content} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {/* Responsibilities Card */}
                <div className="mt-2 rounded-2xl bg-white/[0.03] p-6">
                    <CardHeader
                        title="Key Responsibilities"
                        icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg>}
                        iconColor="text-[#15FBFF]"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {data.keyResponsibilities && data.keyResponsibilities.length > 0 ? (
                            data.keyResponsibilities.map((r, i) => (
                                <div key={i} className="flex items-center gap-4 p-5 bg-white/[0.02] rounded-xl group">
                                    <div className="w-8 h-8 rounded-full bg-[#15FBFF]/10 text-[#15FBFF] flex items-center justify-center text-xs font-bold group-hover:bg-[#15FBFF] group-hover:text-black transition-colors">
                                        {i + 1}
                                    </div>
                                    <span className="text-gray-300 text-sm font-medium group-hover:text-white transition-colors">{r}</span>
                                </div>
                            ))
                        ) : (
                            <div className="flex items-center gap-4 p-5 bg-white/[0.02] rounded-xl">
                                <div className="w-8 h-8 rounded-full bg-gray-700/10 text-gray-600 flex items-center justify-center text-xs font-bold">
                                    -
                                </div>
                                <span className="text-gray-600 text-sm font-medium italic">No data</span>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </SimpleTabContent>
    );
};
